import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { updatesService } from 'services';
import { useAuth } from './AuthContext.jsx';

/**
 * The one-time "what's new" banner.
 *
 * ── Why this is so much smaller than public/updates.js ───────────────────────
 * That file re-mounts itself with a MutationObserver that also watches
 * data-theme, because the content column is rebuilt by every render and swapped
 * wholesale on a theme change. LEARNINGS.md records four separate bugs from that
 * one mechanism — a guard that made the banner appear in only the theme it first
 * mounted into, a version that squashed the V3 layout, and two more.
 *
 * In React the banner is a component in the layout. It re-renders where it is
 * declared, so the observer, the re-mount logic and that whole bug class are
 * simply gone. Nothing here replaces them because nothing needs to.
 *
 * Two behaviours from the old file ARE deliberately kept:
 *   - It never self-starts on page load. It waits for a signed-in session,
 *     because the fetch 401s on the login screen and the release would be lost
 *     for that user.
 *   - It fails silently. A changelog must never be why the page looks broken.
 *
 * The read marker is server-side and per username, so it survives
 * localStorage.clear() in logout().
 */

const UpdatesContext = createContext(null);

export function UpdatesProvider({ children }) {
  const { isSignedIn } = useAuth();
  const [entries, setEntries] = useState([]);
  const [topSeq, setTopSeq] = useState(null);

  useEffect(() => {
    if (!isSignedIn) { setEntries([]); setTopSeq(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await updatesService.getUpdates();
        if (cancelled || !res?.updates?.length) return;
        setEntries(res.updates);
        setTopSeq(res.latestSeq);
      } catch { /* silent by design */ }
    })();
    return () => { cancelled = true; };
  }, [isSignedIn]);

  const dismiss = useCallback(async () => {
    // Acknowledge latestSeq, not the last entry shown — the same value the old
    // client sent. Anything published but filtered out of this user's list (an
    // admin-only entry) is marked read too, which is intended: they were never
    // going to be shown it. The server clamps the value regardless.
    const seq = topSeq;
    setEntries([]); // optimistic: never make someone wait to dismiss a notice
    if (seq == null) return;
    try { await updatesService.markUpdatesSeen({ seq }); } catch { /* silent */ }
  }, [topSeq]);

  const value = useMemo(() => ({ entries, dismiss, hasUpdates: entries.length > 0 }), [entries, dismiss]);

  return <UpdatesContext.Provider value={value}>{children}</UpdatesContext.Provider>;
}

export function useUpdates() {
  const ctx = useContext(UpdatesContext);
  if (!ctx) throw new Error('useUpdates must be used inside <UpdatesProvider>');
  return ctx;
}
